import { Column, Entity, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { RouteTimeClassificationEnum } from '../../types/route-time-classification';
import { BaseEntity } from './base.entity';
import { LocationEntity } from './location.entity';
import { RouteClientEntity } from './route-client.entity';
import { TripEntity } from './trip.entity';
import { UserEntity } from './user.entity';
import { WarehouseEntity } from './warehouse.entity';

/**
 * The recurring delivery-route definition. A Route is a named, geographically
 * concentrated list of clients that get delivered to on some schedule (M/W/F,
 * biweekly Fridays, etc.) — see `recurrenceRule`. Each `Trip` is one day's
 * actual run of a Route.
 *
 * Vehicles are NOT a separate entity in this codebase — they are `Location`
 * rows with `type='vehicle'`. Hence `fk_location__id_default_vehicle` rather
 * than a `fk_vehicle__id` (the table doesn't exist).
 */
@Entity('route')
export class RouteEntity extends BaseEntity {
	@Column({ name: 'name', type: 'varchar', length: 255 })
	public name!: string;

	@Column({ name: 'description', type: 'text', nullable: true })
	public description?: string;

	@Column({
		name: 'fk_user__id_default_driver',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public defaultDriverUserId?: number;

	@Column({
		name: 'fk_location__id_default',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public defaultLocationId?: number;

	@Column({
		name: 'fk_warehouse__id_default',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public defaultWarehouseId?: number;

	@Column({
		name: 'fk_location__id_default_vehicle',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public defaultVehicleId?: number;

	@Column({
		name: 'recurrenceRule',
		type: 'varchar',
		length: 500,
		nullable: true,
	})
	public recurrenceRule?: string;

	/**
	 * Date this route becomes eligible to dispatch. The dispatch wizard
	 * only surfaces this route on dates `>= effectiveDate`. Combined
	 * with `expirationDate` and `recurrenceRule`, the route runs on a
	 * given date D iff:
	 *   `effectiveDate <= D <= (expirationDate ?? +∞)` AND
	 *   RRULE generates an occurrence on D.
	 *
	 * Allows scheduled updates ("change route X starting next month")
	 * and archiving (set `expirationDate` to lock the route out of
	 * future dispatches without deleting historical trip references).
	 * Replaces the previous `recurrenceStartDate` column.
	 */
	@Column({ name: 'effectiveDate', type: 'date', nullable: true })
	public effectiveDate?: Date;

	/**
	 * Last date this route is eligible to dispatch. NULL = never
	 * expires (the common case). Set to a date in the future to schedule
	 * a route's archival; set to a past date to retire it immediately.
	 */
	@Column({ name: 'expirationDate', type: 'date', nullable: true })
	public expirationDate?: Date;

	@Column({ name: 'defaultStartTime', type: 'time', nullable: true })
	public defaultStartTime?: string;

	@Column({ name: 'isActive', type: 'boolean', default: true })
	public isActive!: boolean;

	/**
	 * Day/night classification (Phase C Rule 3). The dispatch wizard
	 * filters (client, route) pairs against this column and
	 * `Client.nightTimeEligibility` — a DAY_ONLY client can't be on
	 * a NIGHT route, etc. Defaults to DAY so existing routes stay
	 * compatible with the DAY_ONLY default on Client.
	 */
	@Column({
		name: 'timeClassification',
		type: 'enum',
		enum: RouteTimeClassificationEnum,
		default: RouteTimeClassificationEnum.DAY,
	})
	public timeClassification!: RouteTimeClassificationEnum;

	@ManyToOne(() => UserEntity, { nullable: true })
	@JoinColumn({ name: 'fk_user__id_default_driver' })
	public defaultDriver?: UserEntity;

	@ManyToOne(() => LocationEntity, { nullable: true })
	@JoinColumn({ name: 'fk_location__id_default' })
	public defaultLocation?: LocationEntity;

	@ManyToOne(() => WarehouseEntity, { nullable: true })
	@JoinColumn({ name: 'fk_warehouse__id_default' })
	public defaultWarehouse?: WarehouseEntity;

	@ManyToOne(() => LocationEntity, { nullable: true })
	@JoinColumn({ name: 'fk_location__id_default_vehicle' })
	public defaultVehicle?: LocationEntity;

	@OneToMany(() => RouteClientEntity, (rc) => rc.fkRoute)
	public routeClients?: Array<RouteClientEntity>;

	@OneToMany(() => TripEntity, (trip) => trip.fkRoute)
	public trips?: Array<TripEntity>;
}
